import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useSite } from '../contexts/SiteContext';
import { getFormTracking, submitAoxForm } from '../lib/aoxForms';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiArrowRight, FiX, FiUser, FiMail, FiPhone, FiCheckCircle, FiShield, FiStar } = FiIcons;

const buttonVariants = {
  player: 'w-full justify-center bg-primary-600 hover:bg-primary-700 text-white shadow-lg shadow-primary-600/20 hover:shadow-xl hover:-translate-y-0.5',
  hero: 'justify-center bg-yellow-400 hover:bg-yellow-300 text-blue-950 shadow-xl shadow-black/20 hover:-translate-y-0.5',
  dark: 'justify-center bg-white text-primary-800 hover:bg-primary-50 shadow-xl hover:-translate-y-0.5',
  outline: 'justify-center bg-white border-2 border-primary-200 text-primary-700 hover:border-primary-500 hover:bg-primary-50 hover:-translate-y-0.5',
  compact: 'justify-center bg-primary-600 hover:bg-primary-700 text-white shadow-md hover:-translate-y-0.5',
};

const SponsorshipCTA = ({ player = null, sourceLocation = 'website', variant = 'compact', label, className = '' }) => {
  const { siteConfig } = useSite();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const isPlayerRequest = Boolean(player?.id);
  const buttonLabel = label || (isPlayerRequest ? 'Become a Sponsor' : 'Γίνε Χορηγός');

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') setIsOpen(false); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const openModal = (event) => {
    if (event) { event.preventDefault(); event.stopPropagation(); }
    setError('');
    setSubmitted(false);
    setIsOpen(true);
  };

  const closeModal = () => {
    if (submitting) return;
    setIsOpen(false);
    setError('');
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        request_scope: isPlayerRequest ? 'player' : 'club',
        player_id: isPlayerRequest ? player.id : null,
        player_name: isPlayerRequest ? player.name : null,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        ...getFormTracking(sourceLocation),
      };

      if (!payload.name || !payload.email || !payload.phone) {
        throw new Error('Συμπλήρωσε και τα τρία πεδία για να επικοινωνήσουμε μαζί σου.');
      }

      await submitAoxForm({
        formType: 'sponsorship',
        payload,
        siteConfig,
        action: 'aox_sponsorship',
      });

      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '' });
    } catch (err) {
      console.error('Sponsorship request failed:', err);
      setError(err?.message || 'Δεν μπορέσαμε να στείλουμε το ενδιαφέρον σου. Δοκίμασε ξανά σε λίγο.');
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <AnimatePresence>
      {isOpen && (
        <motion.div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeModal}>
          <div className="absolute inset-0 bg-secondary-900/80 backdrop-blur-md" />
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }} transition={{ type: 'spring', stiffness: 260, damping: 24 }} onClick={(event) => event.stopPropagation()} className="relative w-full max-w-xl overflow-hidden rounded-[2rem] bg-white shadow-2xl">
            <div className="relative overflow-hidden bg-gradient-to-br from-secondary-900 via-primary-900 to-primary-700 px-6 py-7 text-white sm:px-8 sm:py-9">
              <button type="button" onClick={closeModal} className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/20" aria-label="Κλείσιμο"><SafeIcon icon={FiX} className="h-5 w-5" /></button>
              <div className="relative z-10 pr-10">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.18em] text-blue-100"><SafeIcon icon={isPlayerRequest ? FiStar : FiShield} className="h-4 w-4" />{isPlayerRequest ? 'Στήριξε μια παίκτρια' : 'Στήριξε το γυναικείο ποδόσφαιρο'}</div>
                <h2 className="text-2xl font-black leading-tight sm:text-3xl">{isPlayerRequest ? `Στάσου δίπλα στη διαδρομή της ${player.name}` : 'Γίνε κομμάτι της διαδρομής μας'}</h2>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-blue-100 sm:text-base">{isPlayerRequest ? `Το ενδιαφέρον σου θα καταγραφεί ειδικά για την ${player.name}, ώστε να ξέρουμε ακριβώς ποια παίκτρια θέλεις να στηρίξεις.` : 'Η στήριξή σου γίνεται προπόνηση, εξοπλισμός, μετακινήσεις και περισσότερες ευκαιρίες για την ομάδα.'}</p>
              </div>
            </div>

            <div className="px-6 py-7 sm:px-8">
              {submitted ? (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="py-5 text-center">
                  <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-green-600"><SafeIcon icon={FiCheckCircle} className="h-9 w-9" /></div>
                  <h3 className="text-2xl font-black text-gray-900">Σε ευχαριστούμε.</h3>
                  <p className="mx-auto mt-3 max-w-md leading-relaxed text-gray-600">{isPlayerRequest ? `Καταγράψαμε το ενδιαφέρον σου για την ${player.name}. Θα επικοινωνήσουμε μαζί σου σύντομα.` : 'Καταγράψαμε το ενδιαφέρον σου για στήριξη της ομάδας. Θα επικοινωνήσουμε μαζί σου σύντομα.'}</p>
                  <button type="button" onClick={closeModal} className="mt-6 rounded-xl bg-primary-600 px-7 py-3 font-bold text-white transition hover:bg-primary-700">Τέλεια</button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <p className="mb-5 text-sm leading-relaxed text-gray-600">Μόνο τρία στοιχεία. Καμία τιμή, καμία δέσμευση. Μιλάμε πρώτα και βρίσκουμε μαζί τον τρόπο που ταιριάζει.</p>
                  {isPlayerRequest && <div className="rounded-xl border border-primary-100 bg-primary-50 px-4 py-3 text-sm font-bold text-primary-800">Ενδιαφέρον για παίκτρια: {player.name}</div>}
                  <label className="block"><span className="mb-1.5 block text-sm font-bold text-gray-800">Όνομα</span><div className="relative"><SafeIcon icon={FiUser} className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" /><input type="text" name="name" value={formData.name} onChange={handleChange} autoComplete="name" required maxLength={160} placeholder="Το όνομά σου" className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-12 pr-4 text-gray-900 outline-none transition focus:border-primary-400 focus:bg-white focus:ring-4 focus:ring-primary-100" /></div></label>
                  <label className="block"><span className="mb-1.5 block text-sm font-bold text-gray-800">Email</span><div className="relative"><SafeIcon icon={FiMail} className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" /><input type="email" name="email" value={formData.email} onChange={handleChange} autoComplete="email" required maxLength={254} placeholder="name@example.com" className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-12 pr-4 text-gray-900 outline-none transition focus:border-primary-400 focus:bg-white focus:ring-4 focus:ring-primary-100" /></div></label>
                  <label className="block"><span className="mb-1.5 block text-sm font-bold text-gray-800">Τηλέφωνο</span><div className="relative"><SafeIcon icon={FiPhone} className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" /><input type="tel" name="phone" value={formData.phone} onChange={handleChange} autoComplete="tel" required maxLength={60} placeholder="69..." className="w-full rounded-xl border border-gray-200 bg-gray-50 py-3.5 pl-12 pr-4 text-gray-900 outline-none transition focus:border-primary-400 focus:bg-white focus:ring-4 focus:ring-primary-100" /></div></label>
                  {siteConfig?.recaptcha_enabled && <p className="flex items-center justify-center gap-2 text-xs text-gray-400"><SafeIcon icon={FiShield} className="h-4 w-4" />Προστασία reCAPTCHA v3 ενεργή</p>}
                  {error && <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</div>}
                  <button type="submit" disabled={submitting} className="mt-2 inline-flex w-full items-center justify-center rounded-xl bg-primary-600 px-6 py-4 text-base font-black text-white shadow-lg shadow-primary-600/20 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60">{submitting ? 'Αποστολή...' : 'Θέλω να στηρίξω'}{!submitting && <SafeIcon icon={FiHeart} className="ml-2 h-5 w-5" />}</button>
                  <p className="px-2 text-center text-[11px] leading-relaxed text-gray-400">Με την αποστολή συμφωνείς να επικοινωνήσουμε μαζί σου αποκλειστικά σχετικά με το ενδιαφέρον σου για χορηγία.</p>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <button type="button" onClick={openModal} className={`inline-flex items-center rounded-xl px-5 py-3 font-black transition-all duration-200 ${buttonVariants[variant] || buttonVariants.compact} ${className}`}>
        <SafeIcon icon={FiHeart} className="mr-2 h-5 w-5" />{buttonLabel}<SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
      </button>
      {typeof document !== 'undefined' ? createPortal(modal, document.body) : null}
    </>
  );
};

export default SponsorshipCTA;
